"use client";

import { Code, Cpu, Search, Smartphone, Target, Zap } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const features = [{
  index: "01",
  title: "Site speed & Core Web Vitals",
  description: "Sub-second load times and optimized rendering pipelines ensuring instant AI crawler response and top performance metrics.",
  icon: Zap
}, {
  index: "02",
  title: "Mobile-first responsive UX",
  description: "Flawless rendering and layout stability across every screen size, structured for high conversion and minimal friction.",
  icon: Smartphone
}, {
  index: "03",
  title: "Conversion paths & CTAs",
  description: "Strategic lead flows and high-intent conversion architecture designed to capture AI-referred traffic seamlessly.",
  icon: Target
}, {
  index: "04",
  title: "Structured data & schema",
  description: "Advanced JSON-LD semantic markup providing generative engines with explicit entity clarity and service relationships.",
  icon: Code
}, {
  index: "05",
  title: "Content architecture for AI search",
  description: "Information hierarchies tailored for LLM extraction, prompt alignment, and conversational direct answers.",
  icon: Cpu
}, {
  index: "06",
  title: "Technical SEO foundations",
  description: "Bulletproof crawling rules, canonical structures, and server response optimization built for modern Search Experience Platforms.",
  icon: Search
}];
export default function Optimization() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLElement>({
    threshold: 0.15
  });
  return <section data-section-id="31003" id="technicaloptimizationfeatures" ref={ref} className={`bg-[hsl(240_10%_7%)] text-foreground py-24 md:py-36 px-4 sm:px-6 lg:px-8 transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
      <div className="max-w-7xl mx-auto">
        <div className="text-center max-w-3xl mx-auto mb-16 md:mb-24">
          <span className="text-xs font-mono tracking-[0.25em] uppercase text-[var(--accent-deep-red)] font-semibold block mb-4">
            WHAT WE OPTIMIZE
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-default font-bold tracking-tight leading-tight text-white">
            Engineering your site for{" "}
            <span className="font-serif italic text-[var(--accent-deep-red)] font-normal">
              maximum
            </span>{" "}
            AI search dominance
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground font-sans max-w-2xl mx-auto">
            We rebuild your technical layer so generative engines, web crawlers, and AI agents can read, index, and recommend your services effortlessly.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {features.map((feature, i) => {
          const IconComponent = feature.icon;
          return <div key={feature.index} data-index={i} className="group relative rounded-2xl border border-border/20 bg-[hsl(240_10%_7%)] p-8 hover:-translate-y-1 transition-all duration-200 ease-out shadow-md hover:shadow-[inset_0_0_24px_rgba(168,32,54,0.12)] flex flex-col justify-between overflow-hidden">
                <div>
                  <div className="flex items-center justify-between mb-6">
                    <span className="font-mono text-sm font-semibold text-[var(--accent-deep-red)]">
                      {feature.index}
                    </span>
                    <div className="p-2.5 rounded-xl bg-accent/10 text-[var(--accent-deep-red)] group-hover:bg-[var(--accent-deep-red)] group-hover:text-white transition-colors duration-200">
                      <IconComponent className="size-5" />
                    </div>
                  </div>

                  <h3 className="text-xl font-default font-semibold text-white mb-3 group-hover:text-[var(--accent-deep-red)] transition-colors duration-200">
                    {feature.title}
                  </h3>

                  <p className="text-sm text-muted-foreground leading-relaxed font-sans">
                    {feature.description}
                  </p>
                </div>

                <div className="mt-8 pt-4 border-t border-border/10 flex items-center text-xs font-mono text-muted-foreground/70 group-hover:text-muted-foreground transition-colors">
                  <span>OPTIMIZATION LAYER</span>
                </div>
              </div>;
        })}
        </div>
      </div>
    </section>;
}